/* eslint-disable @typescript-eslint/ban-ts-comment */
import { defineStore } from "pinia";

import { type ScheduleState, type AppointmentFilters } from "./Models/State";
import type { GetAppointmentsParams } from "@/Modules/Models/Api/Schedule";
import type { AppointmentUIQuery } from "./Components/AppointmentForm.vue";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { type Appointment, type PatientWithAppointment } from "@/Modules/Models/Domain/Schedule";
import type { AppointmentApiQuery } from "@/Services/TherapyApi/Schedule";
import { CALENDAR_VIEW } from "@/Components/Extend/Calendar/interfaces";

import getPatientsAH from "./ActionHandlers/GetPatients";
import CreateAppointmentActionHandler from "./ActionHandlers/CreateAppointment";
import DeleteAppointmentActionHandler from "@/Modules/Schedule/ActionHandlers/DeleteAppointment";
import GetAppointmentsActionHandler from "@/Modules/Schedule/ActionHandlers/GetAppointments";
import UpdateAppointmentActionHandler from "./ActionHandlers/UpdateAppointment";
import getAppointmentAH from "./ActionHandlers/GetAppointment";

import { isValidString, removeAccent } from "@/Utils/String";
import { hasValue } from "@/Utils/Objects";
import { dateToSecondsOnlyDate, unixUTC } from "@/Utils/Date";

const ScheduleState: ScheduleState = {
  isLoading: false,
  isLoadingAppointment: false,
  isLoadingPatients: false,
  notification: null,
  appointments: null,
  appointment: null,
  appointmentEdited: null,
  patients: [],
  filters: {},
  dayClicked: null,
  halfHourClicked: null,
  currentCalendarView: CALENDAR_VIEW.WEEK,
};

export const useScheduleStore = defineStore(StateNamespace.SCHEDULE, {
  state: () => ({ ...ScheduleState }),
  getters: {
    filteredAppointments(): Appointment[] {
      if (!hasValue(this.appointments)) return [];
      if (Object.keys(this.filters).length === 0) return this.appointments;

      let filtered: Appointment[] = this.appointments;

      if (hasValue(this.filters.patientUuid)) {
        filtered = filtered.filter((appointment) => appointment.patientUuid === this.filters.patientUuid);
      }

      return filtered;
    },

    getFilteredPatientsByName(): (
      filterValue: string,
      list: PatientWithAppointment[]
    ) => PatientWithAppointment[] {
      return (filterValue: string, list: PatientWithAppointment[]): PatientWithAppointment[] => {
        if (!isValidString(filterValue)) return list;

        return list.filter((p) =>
          removeAccent(p.username.toLowerCase()).includes(removeAccent(filterValue.toLowerCase()))
        );
      };
    },

    filteredNextAppointments(): Appointment[] {
      return this.filteredAppointments.filter((appointment) => appointment.startAt >= unixUTC());
    },

    isClickedDay(): (day: Date) => boolean {
      return (day: Date) => {
        return hasValue(this.dayClicked)
          ? dateToSecondsOnlyDate(day) === dateToSecondsOnlyDate(this.dayClicked)
          : false;
      };
    },

    isClickedHalfHour(): (dayIndex: number | null, halfHour: number) => boolean {
      return (dayIndex: number | null, halfHour: number) => {
        if (!hasValue(this.halfHourClicked)) return false;

        //For day view the "dayIndex" is null
        if (hasValue(dayIndex)) {
          return this.halfHourClicked.dayIndex === dayIndex && this.halfHourClicked.halfHour === halfHour;
        } else {
          return this.halfHourClicked.halfHour === halfHour;
        }
      };
    },
  },

  actions: {
    updateFilter(key: keyof AppointmentFilters, value?: unknown) {
      // @ts-ignore
      this.filters[key] = value;
    },

    createAppointment(appointmentQuery: AppointmentApiQuery) {
      const createAppointmentActionHandler = new CreateAppointmentActionHandler(this);

      return createAppointmentActionHandler.handle(appointmentQuery);
    },

    updateAppointment(appointmentQuery: AppointmentApiQuery, appointmentUuid: string) {
      return UpdateAppointmentActionHandler(appointmentQuery, appointmentUuid);
    },

    editAppointment(appointment: AppointmentUIQuery | null) {
      this.appointmentEdited = appointment;
    },

    deleteAppointment(appointmentUuid: string) {
      return DeleteAppointmentActionHandler(appointmentUuid);
    },

    selectAppointment(appointment: Appointment | null) {
      this.appointment = appointment;
    },

    fetchAppointmentsList(appointmentsParams?: GetAppointmentsParams): Promise<Appointment[]> {
      return GetAppointmentsActionHandler(appointmentsParams);
    },

    fetchAppointment(appointmentUuid: string) {
      return getAppointmentAH(appointmentUuid);
    },

    fetchPatients() {
      getPatientsAH();
    },
  },
});

export type ScheduleStore = ReturnType<typeof useScheduleStore>;

export default useScheduleStore;
